import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { alertRuleApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { PLATOON_PHASES, PHASE_META, type PlatoonPhase } from '../types/domain';

type RuleKind = 'GEOFENCE_DWELL' | 'NO_COMMS' | 'BATTERY_LOW' | 'PHASE_STUCK' | 'ACK_SLA';
type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';

interface AlertRule {
  id: string;
  kind: RuleKind;
  params: Record<string, unknown> | null;
  severity: Severity;
  unitId: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const KIND_META: Record<RuleKind, { label: string; hint: string }> = {
  GEOFENCE_DWELL: { label: 'Geofence Dwell', hint: 'Platoon inside a geofence longer than N minutes.' },
  NO_COMMS: { label: 'No Comms', hint: 'No location/heartbeat for longer than N minutes.' },
  BATTERY_LOW: { label: 'Battery Low', hint: 'Device battery below the threshold %.' },
  PHASE_STUCK: { label: 'Phase Stuck', hint: 'Platoon held in a phase longer than N minutes.' },
  ACK_SLA: { label: 'ACK SLA', hint: 'Ack-required report unacknowledged beyond N minutes.' },
};
const KINDS = Object.keys(KIND_META) as RuleKind[];
const SEVERITIES: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

const SEVERITY_STYLE: Record<Severity, string> = {
  CRITICAL: 'text-severity-critical border-severity-critical/40',
  HIGH: 'text-severity-high border-severity-high/40',
  MEDIUM: 'text-severity-medium border-severity-medium/40',
  LOW: 'text-severity-low border-severity-low/40',
  INFO: 'text-severity-info border-severity-info/40',
};

function describeParams(kind: RuleKind, params: Record<string, unknown> | null): string {
  if (!params) return '—';
  switch (kind) {
    case 'BATTERY_LOW': return `< ${params.threshold ?? '?'}%`;
    case 'PHASE_STUCK': return `${PHASE_META[params.phase as PlatoonPhase]?.label ?? params.phase} > ${params.minutes ?? '?'}m`;
    default: return `> ${params.minutes ?? '?'} min`;
  }
}

// ─── Rule editor modal ───────────────────────────────────────────────────────

function RuleEditor({ rule, onClose }: { rule: AlertRule | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const units = useUnitStore((s) => s.units);
  const isEdit = !!rule;

  const [kind, setKind] = useState<RuleKind>(rule?.kind ?? 'NO_COMMS');
  const [severity, setSeverity] = useState<Severity>(rule?.severity ?? 'MEDIUM');
  const [unitId, setUnitId] = useState<string>(rule?.unitId ?? '');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [minutes, setMinutes] = useState<number>(Number((rule?.params as any)?.minutes) || 30);
  const [threshold, setThreshold] = useState<number>(Number((rule?.params as any)?.threshold) || 15);
  const [phase, setPhase] = useState<PlatoonPhase>(((rule?.params as any)?.phase as PlatoonPhase) || 'EXECUTING');

  const selectableUnits = units.filter((u) => u.role !== 'HQ');

  function buildParams(): Record<string, unknown> {
    switch (kind) {
      case 'BATTERY_LOW': return { threshold };
      case 'PHASE_STUCK': return { phase, minutes };
      default: return { minutes };
    }
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = { params: buildParams(), severity, unitId: unitId || null, enabled };
      return isEdit ? alertRuleApi.update(rule!.id, body) : alertRuleApi.create({ kind, ...body });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Rule updated' : 'Rule created');
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to save rule'),
  });

  return (
    <div className="fixed inset-0 z-[900] bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-lg my-8 bg-command-panel border border-command-border rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-command-border">
          <h2 className="text-base font-semibold text-gray-100">{isEdit ? 'Edit Alert Rule' : 'New Alert Rule'}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-command-border/60" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <label className="block">
            <span className="block text-[11px] font-tactical text-gray-400 mb-1">RULE KIND</span>
            <select value={kind} disabled={isEdit} onChange={(e) => setKind(e.target.value as RuleKind)}
              className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm disabled:opacity-60">
              {KINDS.map((k) => <option key={k} value={k}>{KIND_META[k].label}</option>)}
            </select>
            <span className="block mt-1 text-[11px] text-gray-500">{KIND_META[kind].hint}</span>
          </label>

          {/* Kind-specific params */}
          {kind === 'BATTERY_LOW' ? (
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">THRESHOLD (%)</span>
              <input type="number" min={1} max={100} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {kind === 'PHASE_STUCK' && (
                <label className="block">
                  <span className="block text-[11px] font-tactical text-gray-400 mb-1">PHASE</span>
                  <select value={phase} onChange={(e) => setPhase(e.target.value as PlatoonPhase)}
                    className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
                    {PLATOON_PHASES.map((p) => <option key={p} value={p}>{PHASE_META[p].label}</option>)}
                  </select>
                </label>
              )}
              <label className="block">
                <span className="block text-[11px] font-tactical text-gray-400 mb-1">MINUTES</span>
                <input type="number" min={1} max={1440} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">SEVERITY</span>
              <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">UNIT SCOPE</span>
              <select value={unitId} onChange={(e) => setUnitId(e.target.value)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
                <option value="">All units</option>
                {selectableUnits.map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-gray-600 w-4 h-4" />
            Enabled
          </label>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-command-border">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-100 border border-command-border">Cancel</button>
          <button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90 disabled:opacity-50">
            {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AlertRulesPage() {
  const { user } = useAuthStore();
  const { isHQ, units, fetchUnits } = useUnitStore();
  const userIsHQ = isHQ(user?.unitId ?? null);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ open: boolean; rule: AlertRule | null }>({ open: false, rule: null });

  useEffect(() => {
    if (userIsHQ && units.length === 0) fetchUnits();
  }, [userIsHQ, units.length, fetchUnits]);

  const rulesQ = useQuery({
    queryKey: ['alert-rules'],
    queryFn: async () => (await alertRuleApi.getAll()).data?.data?.rules as AlertRule[],
    enabled: userIsHQ,
    retry: false,
    refetchInterval: 20000,
  });

  const toggleMutation = useMutation({
    mutationFn: (r: AlertRule) => alertRuleApi.update(r.id, { enabled: !r.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to toggle rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertRuleApi.delete(id),
    onSuccess: () => {
      toast.success('Rule deleted');
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to delete rule'),
  });

  if (!userIsHQ) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-display font-bold text-gray-100">Alert Rules</h1>
        <p className="text-gray-500 mt-2 text-sm">Superuser (Brigade HQ) only.</p>
      </div>
    );
  }

  const rules = rulesQ.data ?? [];
  const unitCode = (id: string | null) => (id ? units.find((u) => u.id === id)?.code ?? id : 'All units');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-display font-bold text-gray-100">Alert Rules</h1>
          <p className="text-gray-500 text-[11px]">Declarative watchdogs · evaluated every 60 s server-side</p>
        </div>
        <button type="button" onClick={() => setEditing({ open: true, rule: null })}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-command-accent text-command-bg text-sm font-semibold hover:bg-command-accent/90">
          <PlusIcon className="w-4 h-4" /> New Rule
        </button>
      </div>

      <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-2 px-3 font-semibold">Kind</th>
                <th className="text-left py-2 px-3 font-semibold">Condition</th>
                <th className="text-left py-2 px-3 font-semibold">Severity</th>
                <th className="text-left py-2 px-3 font-semibold">Unit</th>
                <th className="text-center py-2 px-3 font-semibold">Enabled</th>
                <th className="text-right py-2 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b border-command-border/60">
                  <td className="py-2 px-3 font-semibold text-gray-100">{KIND_META[r.kind]?.label ?? r.kind}</td>
                  <td className="py-2 px-3 text-gray-300 font-tactical">{describeParams(r.kind, r.params)}</td>
                  <td className="py-2 px-3">
                    <span className={clsx('px-2 py-0.5 rounded-full text-[10px] font-bold border', SEVERITY_STYLE[r.severity])}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-400">{unitCode(r.unitId)}</td>
                  <td className="py-2 px-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleMutation.mutate(r)}
                      className={clsx('relative w-10 h-5 rounded-full transition-colors', r.enabled ? 'bg-command-accent' : 'bg-command-border')}
                      aria-label={r.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      <span className={clsx('absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all', r.enabled ? 'left-5' : 'left-0.5')} />
                    </button>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => setEditing({ open: true, rule: r })}
                        className="p-1.5 rounded hover:bg-command-border/60" aria-label="Edit rule">
                        <PencilSquareIcon className="w-4 h-4 text-gray-400" />
                      </button>
                      <button type="button"
                        onClick={() => { if (window.confirm('Delete this alert rule?')) deleteMutation.mutate(r.id); }}
                        className="p-1.5 rounded hover:bg-severity-critical/20" aria-label="Delete rule">
                        <TrashIcon className="w-4 h-4 text-severity-critical" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500 text-sm">
                    {rulesQ.isError ? 'Alert-rules service unavailable.' : 'No alert rules configured yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rules.length > 0 && (
        <p className="text-[10px] text-gray-600">
          Last refreshed {format(new Date(), 'HH:mm:ss')} · rules fire Alerts on the alerts panel when breached.
        </p>
      )}

      {editing.open && (
        <RuleEditor rule={editing.rule} onClose={() => setEditing({ open: false, rule: null })} />
      )}
    </div>
  );
}
